import { useState } from 'react';
import type { ConfigField } from '../../services/source.service';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { Eye, EyeOff, Settings } from 'lucide-react';
import { StreamSelectorDialog } from './StreamSelectorDialog';
import { ConfigFieldDescription } from './ConfigFieldDescription';
import { OptionsMultiSelectField } from './OptionsMultiSelectField';
import { JsonConfigField } from './JsonConfigField';
import { KeypairRotationField } from './KeypairRotationField';

interface ConfigFieldEditorProps {
  configField: ConfigField;
  value: string;
  onChange: (value: string) => void;
  secure?: boolean;
  sourceId?: string;
  legacyStreams?: string;
  /** Current (possibly unsaved) form config, for fields that act on sibling values. */
  config?: Record<string, string>;
}

export function ConfigFieldEditor({ configField, value, onChange, secure, sourceId, legacyStreams, config }: ConfigFieldEditorProps) {
  const [showSecure, setShowSecure] = useState(false);
  const [streamSelectorOpen, setStreamSelectorOpen] = useState(false);

  if (configField.field_type === 'JSON') {
    return (
      <JsonConfigField
        label={configField.label}
        value={value || ''}
        onChange={onChange}
        description={configField.description}
      />
    );
  }

  if (configField.field_type === 'LONG_TEXT') {
    return (
      <div className="space-y-2">
        <Label>{configField.label}</Label>
        <div className="relative">
          <Textarea
            value={secure && !showSecure ? (value ? '••••••••' : '') : (value || '')}
            onChange={(e) => {
              if (secure && !showSecure) {
                setShowSecure(true);
                return;
              }
              onChange(e.target.value);
            }}
            onFocus={() => { if (secure && !showSecure && value) setShowSecure(true); }}
            rows={4}
            placeholder={secure ? 'Enter new value...' : undefined}
          />
          {secure && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute top-1 right-1 h-7 w-7 p-0"
              onClick={() => setShowSecure(!showSecure)}
            >
              {showSecure ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          )}
        </div>
        <ConfigFieldDescription description={configField.description} />
      </div>
    );
  }

  if (configField.field_type === 'MULTI_SELECT') {
    const selectedValues = value ? value.split(',').filter(Boolean) : [];
    const choices = configField.choices || [];

    const toggleChoice = (choice: string) => {
      const current = new Set(selectedValues);
      if (current.has(choice)) {
        current.delete(choice);
      } else {
        current.add(choice);
      }
      onChange(Array.from(current).join(','));
    };

    return (
      <div className="space-y-2">
        <Label>{configField.label}</Label>
        <div className="border rounded-md p-2 space-y-1 max-h-48 overflow-y-auto">
          {choices.map((choice) => (
            <label key={choice} className="flex items-center gap-2 cursor-pointer px-2 py-1 hover:bg-accent/50 rounded-sm">
              <input
                type="checkbox"
                checked={selectedValues.includes(choice)}
                onChange={() => toggleChoice(choice)}
                className="rounded border-input"
              />
              <span className="text-sm">{choice}</span>
            </label>
          ))}
        </div>
        {selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selectedValues.map((v) => (
              <Badge key={v} variant="secondary" className="text-xs">{v}</Badge>
            ))}
          </div>
        )}
        <ConfigFieldDescription description={configField.description} />
      </div>
    );
  }

  if (configField.field_type === 'SINGLE_SELECT') {
    const choices = configField.choices || [];
    const currentValue = value || configField.default_value || '';

    return (
      <div className="space-y-2">
        <Label>{configField.label}</Label>
        <Select value={currentValue} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {choices.map((choice) => (
              <SelectItem key={choice} value={choice}>
                {choice}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ConfigFieldDescription description={configField.description} />
      </div>
    );
  }

  if (configField.field_type === 'MELTANO_STREAMS') {
    // Count selected streams from stored JSON, falling back to legacy comma-separated streams
    let selectedCount = 0;
    let isLegacyFallback = false;
    try {
      const parsed = JSON.parse(value || '{}');
      if (parsed && typeof parsed === 'object') {
        selectedCount = Object.values(parsed).filter((cfg: any) => cfg.selected).length;
      }
    } catch {
      // not valid JSON yet
    }

    if (selectedCount === 0 && legacyStreams) {
      const legacyList = legacyStreams.split(',').map((s) => s.trim()).filter(Boolean);
      selectedCount = legacyList.length;
      isLegacyFallback = selectedCount > 0;
    }

    // Build the initial value for the dialog from legacy streams if meltano_streams is empty
    const dialogValue = (() => {
      if (value) return value;
      if (!legacyStreams) return '';
      const legacyList = legacyStreams.split(',').map((s) => s.trim()).filter(Boolean);
      if (legacyList.length === 0) return '';
      const seeded: Record<string, any> = {};
      for (const s of legacyList) {
        seeded[s] = {
          selected: true,
          replication_method: 'FULL_TABLE',
          replication_key: null,
        };
      }
      return JSON.stringify(seeded);
    })();

    return (
      <div className="space-y-2">
        <Label>{configField.label}</Label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {selectedCount > 0
              ? `${selectedCount} stream${selectedCount !== 1 ? 's' : ''} selected${isLegacyFallback ? ' (from legacy config)' : ''}`
              : 'No streams selected'}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setStreamSelectorOpen(true)}
            title="Configure streams"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
        <ConfigFieldDescription description={configField.description} />
        {streamSelectorOpen && sourceId && (
          <StreamSelectorDialog
            sourceId={sourceId}
            open={streamSelectorOpen}
            onOpenChange={setStreamSelectorOpen}
            mode="meltano_streams"
            value={dialogValue}
            onSave={(val) => onChange(val)}
          />
        )}
      </div>
    );
  }

  if (configField.field_type === 'OPTIONS_MULTISELECT') {
    return (
      <OptionsMultiSelectField
        configField={configField}
        value={value}
        onChange={onChange}
        sourceId={sourceId}
      />
    );
  }

  if (configField.field_type === 'KEYPAIR_ROTATION') {
    return <KeypairRotationField configField={configField} sourceId={sourceId} config={config} />;
  }

  if (configField.field_type === 'NUMBER') {
    return (
      <div className="space-y-2">
        <Label>{configField.label}</Label>
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={configField.default_value ? `Default: ${configField.default_value}` : 'Leave blank for default'}
        />
        <ConfigFieldDescription description={configField.description} />
      </div>
    );
  }

  // Default: SHORT_TEXT (or any unrecognized type)
  const isDateField = /date/i.test(configField.name);
  const inputType = secure ? 'password' : isDateField ? 'date' : 'text';

  return (
    <div className="space-y-2">
      <Label>{configField.label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type={inputType}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={secure ? 'Enter new value...' : undefined}
          className="flex-1"
        />
      </div>
      <ConfigFieldDescription description={configField.description} />
    </div>
  );
}
